/**
 * Workspace-specific design helpers (on top of components/em/ui).
 * Matches the EM design handoff: progress bars, KPI cards, filter/segmented
 * pills, project colour badge, money formatting.
 */
import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { X, ChevronDown, MoreVertical } from 'lucide-react';
import { T } from '../../components/em/ui';
import { wsApi, assetUrl } from './wsApi';
import { initials, avatarColor, COLOR_SWATCHES } from './wsConstants';

export const money = (n) => '₹' + Math.round(Number(n) || 0).toLocaleString('en-IN');

// Dark hover tooltip under its child (e.g. "Assignee: Full Name" on an avatar).
// Rendered in a portal at fixed position, so a board column's scroll/overflow
// can't clip it the way a CSS ::after tooltip would be.
export const HoverTip = ({ label, children, style = {} }) => {
  const ref = useRef(null);
  const [pos, setPos] = useState(null);
  const show = () => {
    const r = ref.current?.getBoundingClientRect();
    if (!r) return;
    // Keep the bubble's centre a little way in from the window edges.
    const x = Math.min(Math.max(r.left + r.width / 2, 90), window.innerWidth - 90);
    setPos({ x, y: r.bottom + 6 });
  };
  const hide = () => setPos(null);
  // A scroll moves the anchor; drop the bubble rather than leave it stranded.
  useEffect(() => {
    if (!pos) return undefined;
    window.addEventListener('scroll', hide, true);
    return () => window.removeEventListener('scroll', hide, true);
  }, [pos]);
  if (!label) return children;
  return (
    <span
      ref={ref}
      onMouseEnter={show}
      onMouseLeave={hide}
      aria-label={label}
      style={{ display: 'inline-flex', ...style }}
    >
      {children}
      {pos &&
        createPortal(
          <span
            role="tooltip"
            style={{
              position: 'fixed', left: pos.x, top: pos.y, transform: 'translateX(-50%)', zIndex: 3000,
              background: '#1f2328', color: '#fff', font: `500 12px ${T.font}`, padding: '5px 9px',
              borderRadius: 6, whiteSpace: 'nowrap', pointerEvents: 'none',
              boxShadow: '0 4px 12px rgba(0,0,0,0.18)',
            }}
          >
            {label}
          </span>,
          document.body,
        )}
    </span>
  );
};

// "Assignee: Full Name" (or Collaborator / Watcher) for a task member.
export const taskMemberLabel = (m) => {
  const role = { assignee: 'Assignee', collaborator: 'Collaborator', watcher: 'Watcher' }[m?.member_role] || 'Assignee';
  return `${role}: ${m?.full_name || m?.username || 'Unknown'}`;
};

// Logo/image picker with upload + preview; falls back to a coloured initial.
export const LogoPicker = ({ value, onChange, color = T.orange, label = '', size = 64 }) => {
  const ref = useRef(null);
  const [busy, setBusy] = useState(false);
  const pick = async (file) => {
    if (!file) return;
    setBusy(true);
    try {
      const { url } = await wsApi.uploadImage(file);
      onChange(url);
    } catch { /* surfaced by caller toasts elsewhere */ }
    finally { setBusy(false); }
  };
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
      <div
        onClick={() => ref.current?.click()}
        title="Upload logo"
        style={{
          width: size, height: size, borderRadius: 12, cursor: 'pointer', flexShrink: 0,
          background: value ? '#fff' : color, color: '#fff', overflow: 'hidden',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          border: `1px solid ${T.border}`, font: `700 ${Math.round(size * 0.32)}px ${T.font}`,
        }}
      >
        {value
          ? <img src={assetUrl(value)} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
          : initials(label)}
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
        <button type="button" onClick={() => ref.current?.click()} disabled={busy}
          style={{ border: `1px solid ${T.border}`, background: 'var(--em-bg-card)', color: T.fg2,
            borderRadius: 8, padding: '6px 12px', cursor: 'pointer', font: `600 12px ${T.font}` }}>
          {busy ? 'Uploading…' : value ? 'Replace' : 'Upload logo'}
        </button>
        {value && (
          <button type="button" onClick={() => onChange('')}
            style={{ border: 0, background: 'none', color: T.danger, cursor: 'pointer', font: `500 11px ${T.font}`, textAlign: 'left', padding: 0 }}>
            Remove
          </button>
        )}
      </div>
      <input ref={ref} type="file" accept="image/*" style={{ display: 'none' }}
        onChange={(e) => { pick(e.target.files?.[0]); e.target.value = ''; }} />
    </div>
  );
};

// Single avatar — photo when available, else coloured initials.
export const Avatar = ({ member = {}, size = 28, style = {} }) => {
  const photo = member.profile_photo
    ? assetUrl(member.profile_photo.startsWith('/') ? member.profile_photo : `/${member.profile_photo}`)
    : null;
  const name = member.full_name || member.username || '?';
  return (
    <div title={name}
      style={{ width: size, height: size, borderRadius: '50%',
        background: photo ? '#fff' : avatarColor(name),
        color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center',
        font: `700 ${Math.round(size * 0.4)}px ${T.font}`, overflow: 'hidden', flexShrink: 0, ...style }}>
      {photo ? <img src={photo} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} /> : initials(name)}
    </div>
  );
};

// Overlapping avatar stack — shows up to `max`, then a +N chip with a tooltip.
export const AvatarStack = ({ members = [], max = 4, size = 28 }) => {
  const shown = members.slice(0, max);
  const rest = members.slice(max);
  const restNames = rest.map((m) => m.full_name || m.username).filter(Boolean).join(', ');
  return (
    <div style={{ display: 'flex', alignItems: 'center' }}>
      {shown.map((m, i) => (
        <Avatar key={m.user_id || i} member={m} size={size}
          style={{ marginLeft: i ? -8 : 0, border: '2px solid var(--em-bg-card)' }} />
      ))}
      {rest.length > 0 && (
        <div title={restNames}
          style={{ width: size, height: size, borderRadius: '50%', marginLeft: -8,
            border: '2px solid var(--em-bg-card)', background: T.bgMuted, color: T.fg4,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            font: `700 ${Math.round(size * 0.36)}px ${T.font}`, flexShrink: 0 }}>
          +{rest.length}
        </div>
      )}
    </div>
  );
};

// A person's display name: their full name from the employee record (sent by
// /users as full_name), else the account username.
export const personName = (u) => u?.full_name || u?.username || '';

// A–Z by name, ignoring case — for project dropdowns. The server returns
// projects newest-first, which the Projects grid wants but a picker doesn't.
export const byName = (list = []) =>
  [...list].sort((a, b) =>
    String(a.name || '').localeCompare(String(b.name || ''), undefined, { sensitivity: 'base' }));

// Compact single-select filter. A native <select> lets the browser place its
// list, and a long one (employees, projects) flips ABOVE the field and covers
// it while a short one (teams) opens below. This list always opens below,
// capped at `maxHeight` with its own scroll, so every filter behaves the same.
// options = [{ value, label }]; `allLabel` is the empty-value choice.
// `buttonStyle` restyles the closed field, e.g. to match a form's full-size
// <Select>; the list keeps its compact look either way.
export const FilterSelect = ({
  value, onChange, options = [], allLabel, style = {}, buttonStyle = {}, maxHeight = 200, searchAt = 8,
}) => {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [hi, setHi] = useState(-1);
  const ref = useRef(null);
  const listRef = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    const onDown = (ev) => { if (ref.current && !ref.current.contains(ev.target)) setOpen(false); };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [open]);
  useEffect(() => { if (!open) { setQuery(''); setHi(-1); } }, [open]);

  const all = [{ value: '', label: allLabel }, ...options];
  const q = query.trim().toLowerCase();
  const shown = q ? all.filter((o) => o.value !== '' && String(o.label).toLowerCase().includes(q)) : all;
  const current = all.find((o) => String(o.value) === String(value)) || all[0];

  const pick = (v) => { onChange(v); setOpen(false); };
  const onKey = (e) => {
    if (e.key === 'Escape') { setOpen(false); return; }
    if (!open && (e.key === 'ArrowDown' || e.key === 'Enter' || e.key === ' ')) {
      e.preventDefault(); setOpen(true); return;
    }
    if (!open) return;
    if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
      e.preventDefault();
      const n = shown.length;
      if (!n) return;
      const next = e.key === 'ArrowDown' ? (hi + 1) % n : (hi - 1 + n) % n;
      setHi(next);
      listRef.current?.children[next]?.scrollIntoView({ block: 'nearest' });
    } else if (e.key === 'Enter' && hi >= 0 && shown[hi]) {
      e.preventDefault(); pick(shown[hi].value);
    }
  };

  const row = {
    display: 'block', width: '100%', padding: '7px 10px', margin: 0, border: 0, borderRadius: 6,
    textAlign: 'left', cursor: 'pointer', font: `500 12px ${T.font}`,
    whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
  };

  return (
    <div ref={ref} onKeyDown={onKey} style={{ position: 'relative', ...style }}>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-haspopup="listbox"
        aria-expanded={open}
        title={current.value !== '' ? current.label : undefined}
        style={{
          display: 'block', width: '100%', boxSizing: 'border-box', margin: 0, position: 'relative',
          padding: '7px 26px 7px 10px', borderRadius: 8, border: `1px solid ${T.border}`,
          background: 'var(--em-bg-card)', color: T.fg2, cursor: 'pointer', textAlign: 'left',
          font: `500 12px ${T.font}`, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
          ...buttonStyle,
        }}
      >
        {current.label}
        <ChevronDown size={14} color={T.fg5} style={{ position: 'absolute', right: 8, top: '50%', transform: 'translateY(-50%)' }} />
      </button>
      {open && (
        <div style={{
          position: 'absolute', top: 'calc(100% + 4px)', left: 0, minWidth: '100%', maxWidth: 320, zIndex: 40,
          background: 'var(--em-bg-card)', border: `1px solid ${T.border}`, borderRadius: 10,
          boxShadow: T.shadowMd, padding: 6, boxSizing: 'border-box',
        }}>
          {options.length > searchAt && (
            <input
              autoFocus
              value={query}
              onChange={(e) => { setQuery(e.target.value); setHi(-1); }}
              placeholder="Search…"
              style={{
                width: '100%', boxSizing: 'border-box', margin: '0 0 6px', padding: '6px 10px',
                border: `1px solid ${T.border}`, borderRadius: 7, outline: 'none',
                background: 'var(--em-bg-card)', color: T.fg1, font: `400 12px ${T.font}`,
              }}
            />
          )}
          <div ref={listRef} role="listbox" style={{ maxHeight, overflowY: 'auto' }}>
            {shown.map((o, i) => {
              const on = String(o.value) === String(value);
              return (
                <button
                  key={o.value === '' ? '__all' : o.value}
                  type="button"
                  role="option"
                  aria-selected={on}
                  title={o.label}
                  onClick={() => pick(o.value)}
                  onMouseEnter={() => setHi(i)}
                  style={{
                    ...row,
                    background: i === hi ? T.bgMuted : on ? T.orange50 : 'transparent',
                    color: on ? T.orange : T.fg1,
                  }}
                >
                  {o.label}
                </button>
              );
            })}
            {!shown.length && (
              <div style={{ padding: 10, textAlign: 'center', font: `400 12px ${T.font}`, color: T.fg7 }}>No matches</div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

// Searchable member combobox — dropdown opens on focus/typing; selected people
// render as avatar chips with an × below the field.
// `users` = [{ id, username, full_name, email }]; matches on any of them.
export const MemberCombobox = ({ users = [], selected = [], onToggle, placeholder = 'Search people…' }) => {
  const [qy, setQy] = useState('');
  const [open, setOpen] = useState(false);
  const wrapRef = useRef(null);
  const sel = new Set(selected.map(Number));
  const needle = qy.trim().toLowerCase();
  const filtered = users.filter((u) =>
    !needle ||
    [u.full_name, u.username, u.email].some((v) => String(v || '').toLowerCase().includes(needle)));
  const chips = users.filter((u) => sel.has(Number(u.id)));

  useEffect(() => {
    if (!open) return undefined;
    const onDown = (e) => { if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false); };
    const onKey = (e) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => { document.removeEventListener('mousedown', onDown); document.removeEventListener('keydown', onKey); };
  }, [open]);

  return (
    <div ref={wrapRef} style={{ position: 'relative' }}>
      <input value={qy} placeholder={placeholder}
        onChange={(e) => { setQy(e.target.value); setOpen(true); }}
        onFocus={() => setOpen(true)}
        style={{ width: '100%', boxSizing: 'border-box', border: `1px solid ${T.border}`,
          borderRadius: 8, padding: '8px 11px', font: `400 13px ${T.font}`, outline: 'none',
          background: 'var(--em-bg-card)', color: T.fg1 }} />

      {open && (
        <div style={{ position: 'absolute', top: 'calc(100% + 4px)', left: 0, right: 0, zIndex: 40,
          maxHeight: 220, overflowY: 'auto', background: 'var(--em-bg-card)',
          border: `1px solid ${T.border}`, borderRadius: 10, boxShadow: '0 12px 32px -8px rgba(10,10,10,0.18)' }}>
          {filtered.map((u) => {
            const on = sel.has(Number(u.id));
            return (
              <div key={u.id} onClick={() => onToggle(u.id)}
                style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 11px', cursor: 'pointer',
                  borderBottom: `1px solid ${T.divider}`, background: on ? T.orange50 : 'transparent' }}>
                <Avatar member={u} size={24} />
                <span style={{ flex: 1, minWidth: 0, font: `500 13px ${T.font}`, color: T.fg1, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                  {personName(u)}
                  {u.email && <span style={{ marginLeft: 8, font: `400 11px ${T.font}`, color: T.fg7 }}>{u.email}</span>}
                </span>
                {on && <span style={{ color: T.orange, font: `700 12px ${T.font}` }}>✓</span>}
              </div>
            );
          })}
          {!filtered.length && <div style={{ padding: 14, textAlign: 'center', color: T.fg7, font: `400 12px ${T.font}` }}>No people found.</div>}
        </div>
      )}

      {chips.length > 0 && (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 8 }}>
          {chips.map((u) => (
            <span key={u.id} style={{ display: 'inline-flex', alignItems: 'center', gap: 6,
              padding: '3px 8px 3px 3px', borderRadius: 9999, border: `1px solid ${T.border}`,
              background: 'var(--em-bg-card)', font: `500 12px ${T.font}`, color: T.fg2 }}>
              <Avatar member={u} size={20} />
              {personName(u)}
              <button type="button" title="Remove" onClick={() => onToggle(u.id)}
                style={{ background: 'none', border: 0, cursor: 'pointer', color: T.fg7, display: 'flex', padding: 0 }}>
                <X size={12} />
              </button>
            </span>
          ))}
        </div>
      )}
    </div>
  );
};

// Colour field — shows the current colour; opens a swatch-grid panel on click.
export const ColorSwatchSelect = ({ value, onChange, colors = COLOR_SWATCHES }) => {
  const [open, setOpen] = useState(false);
  const wrapRef = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    const onDown = (e) => { if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false); };
    const onKey = (e) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => { document.removeEventListener('mousedown', onDown); document.removeEventListener('keydown', onKey); };
  }, [open]);

  return (
    <div ref={wrapRef} style={{ position: 'relative' }}>
      <button type="button" onClick={() => setOpen((o) => !o)}
        style={{ display: 'flex', alignItems: 'center', gap: 9, width: '100%', boxSizing: 'border-box',
          border: `1px solid ${T.border}`, borderRadius: 8, padding: '8px 11px', cursor: 'pointer',
          background: 'var(--em-bg-card)', font: `500 13px ${T.font}`, color: T.fg1, textAlign: 'left' }}>
        <span style={{ width: 16, height: 16, borderRadius: '50%', background: value || colors[0], flexShrink: 0, border: '1px solid rgba(0,0,0,0.08)' }} />
        <span style={{ flex: 1 }}>{value || colors[0]}</span>
        <ChevronDown size={14} color={T.fg7} style={{ flexShrink: 0 }} />
      </button>
      {open && (
        <div style={{ position: 'absolute', top: 'calc(100% + 4px)', left: 0, zIndex: 40,
          display: 'grid', gridTemplateColumns: 'repeat(4, 28px)', gap: 8, padding: 12,
          background: 'var(--em-bg-card)', border: `1px solid ${T.border}`, borderRadius: 10,
          boxShadow: '0 12px 32px -8px rgba(10,10,10,0.18)' }}>
          {colors.map((c) => (
            <button key={c} type="button" title={c}
              onClick={() => { onChange(c); setOpen(false); }}
              style={{ width: 28, height: 28, borderRadius: 8, background: c, cursor: 'pointer',
                border: value === c ? '2px solid var(--em-fg1)' : '2px solid transparent', padding: 0 }} />
          ))}
        </div>
      )}
    </div>
  );
};

export const ProgressBar = ({ pct, color = T.orange, height = 6 }) => (
  <div style={{ flex: 1, height, borderRadius: 9999, background: T.bgMuted, overflow: 'hidden', minWidth: 40 }}>
    <div style={{ height: '100%', width: `${Math.max(0, Math.min(100, pct))}%`, background: color, borderRadius: 9999, transition: 'width 300ms' }} />
  </div>
);

export const KpiCard = ({ Icon, label, value, sub, color, bg }) => (
  <div style={{ background: 'var(--em-bg-card)', border: `1px solid ${T.border}`, borderRadius: 14, padding: '18px 20px', boxShadow: T.shadowSm }}>
    <div style={{ width: 38, height: 38, borderRadius: 10, background: bg, display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: 14 }}>
      <Icon size={18} color={color} />
    </div>
    <div style={{ font: `800 26px/1 ${T.font}`, letterSpacing: '-0.03em', color: T.fg1, fontVariantNumeric: 'tabular-nums' }}>{value}</div>
    <div style={{ font: `500 12px ${T.font}`, color: T.fg4, marginTop: 6 }}>{label}</div>
    {sub != null && <div style={{ font: `400 11px ${T.font}`, color: T.fg7, marginTop: 2 }}>{sub}</div>}
  </div>
);

// Pill group — rounded segmented control (filters / view toggles).
export const Pills = ({ options, value, onChange, style = {} }) => (
  <div style={{ display: 'inline-flex', gap: 2, padding: 4, borderRadius: 9999, background: T.bgMuted, ...style }}>
    {options.map((o) => {
      const on = value === o.value;
      const Icon = o.icon;
      return (
        <button key={o.value} onClick={() => onChange(o.value)} style={{
          display: 'inline-flex', alignItems: 'center', gap: 6, padding: '6px 14px', borderRadius: 9999,
          border: 0, cursor: 'pointer', background: on ? 'var(--em-bg-card)' : 'transparent',
          color: on ? T.orange : T.fg5, font: `${on ? 600 : 500} 12px ${T.font}`,
          boxShadow: on ? '0 1px 2px rgba(0,0,0,0.06)' : 'none', transition: 'all .15s',
        }}>
          {Icon && <Icon size={14} />}{o.label}
        </button>
      );
    })}
  </div>
);

export const ColorBadge = ({ color = T.orange, size = 40, label }) => (
  <div style={{ width: size, height: size, borderRadius: 9, background: color, color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', font: `700 ${Math.round(size * 0.4)}px ${T.font}`, flexShrink: 0 }}>
    {label || ''}
  </div>
);

// Hover lift for cards — spread onto a card div alongside its style.
export const hoverLift = {
  onMouseEnter: (e) => {
    e.currentTarget.style.boxShadow = '0 8px 24px -10px rgba(10,10,10,0.18)';
    e.currentTarget.style.transform = 'translateY(-2px)';
  },
  onMouseLeave: (e) => {
    e.currentTarget.style.boxShadow = T.shadowSm;
    e.currentTarget.style.transform = 'none';
  },
};
export const hoverLiftStyle = { transition: 'box-shadow 150ms, transform 150ms' };

// Shimmering skeleton block (one-time keyframes injection).
let skeletonCssInjected = false;
const ensureSkeletonCss = () => {
  if (skeletonCssInjected || typeof document === 'undefined') return;
  skeletonCssInjected = true;
  const el = document.createElement('style');
  el.textContent = `@keyframes ws-shimmer { 0% { background-position: -400px 0; } 100% { background-position: 400px 0; } }`;
  document.head.appendChild(el);
};

export const Skeleton = ({ w = '100%', h = 14, radius = 6, style = {} }) => {
  ensureSkeletonCss();
  return (
    <div style={{ width: w, height: h, borderRadius: radius,
      background: 'linear-gradient(90deg, var(--em-bg-muted, #f3f4f6) 25%, var(--em-bg-head, #ececf0) 37%, var(--em-bg-muted, #f3f4f6) 63%)',
      backgroundSize: '400px 100%', animation: 'ws-shimmer 1.3s ease-in-out infinite', ...style }} />
  );
};

// Card-shaped skeleton for list grids.
export const SkeletonCard = ({ height = 150 }) => (
  <div style={{ background: 'var(--em-bg-card)', border: `1px solid ${T.border}`, borderRadius: 14, padding: 18, boxShadow: T.shadowSm, height, boxSizing: 'border-box' }}>
    <div style={{ display: 'flex', gap: 12, marginBottom: 16 }}>
      <Skeleton w={40} h={40} radius={10} />
      <div style={{ flex: 1 }}>
        <Skeleton w="60%" h={13} style={{ marginBottom: 8 }} />
        <Skeleton w="40%" h={10} />
      </div>
    </div>
    <Skeleton w="100%" h={8} style={{ marginBottom: 12 }} />
    <Skeleton w="50%" h={10} />
  </div>
);

// Grid of skeleton cards sized like the list grids.
export const SkeletonGrid = ({ count = 6, minWidth = 300, height = 150 }) => (
  <div style={{ display: 'grid', gridTemplateColumns: `repeat(auto-fill, minmax(${minWidth}px, 1fr))`, gap: 16 }}>
    {Array.from({ length: count }, (_, i) => <SkeletonCard key={i} height={height} />)}
  </div>
);

/**
 * RowMenu — kebab (⋮) that collapses a row's actions into a dropdown.
 *
 * `items`: [{ key, label, icon, onClick, danger, disabled, hidden }]. Entries
 * with `hidden: true` are dropped, so callers can keep permission checks inline
 * (`hidden: !canApprove`) instead of building the array conditionally.
 *
 * The panel renders through a portal at fixed coordinates rather than as an
 * absolutely-positioned child. List containers here use `overflow: hidden` to
 * clip their rounded corners, which would also clip an in-flow dropdown — and
 * the menu on the last row needs to escape the card entirely. It flips above
 * the button when there isn't room below, and closes on outside click, Escape,
 * scroll, or resize (a fixed panel would otherwise detach from its anchor).
 */
export const RowMenu = ({ items = [], label = 'Actions', size = 16 }) => {
  const btnRef = useRef(null);
  const [pos, setPos] = useState(null);        // null = closed
  const visible = items.filter((i) => !i.hidden);

  const place = () => {
    const r = btnRef.current?.getBoundingClientRect();
    if (!r) return;
    const height = visible.length * 36 + 10;   // ~36px per item + 5px padding each end
    const below = window.innerHeight - r.bottom > height + 8;
    setPos({
      top: below ? r.bottom + 4 : r.top - height - 4,
      right: Math.max(8, window.innerWidth - r.right),
    });
  };

  useEffect(() => {
    if (!pos) return undefined;
    const close = () => setPos(null);
    const onKey = (e) => { if (e.key === 'Escape') close(); };
    document.addEventListener('click', close);
    document.addEventListener('keydown', onKey);
    window.addEventListener('scroll', close, true);   // capture: catches inner scrollers
    window.addEventListener('resize', close);
    return () => {
      document.removeEventListener('click', close);
      document.removeEventListener('keydown', onKey);
      window.removeEventListener('scroll', close, true);
      window.removeEventListener('resize', close);
    };
  }, [pos]);

  if (!visible.length) return null;

  return (
    <>
      <button
        ref={btnRef}
        type="button"
        aria-label={label}
        aria-haspopup="menu"
        aria-expanded={!!pos}
        title={label}
        onClick={(e) => { e.stopPropagation(); if (pos) setPos(null); else place(); }}
        style={{
          // Fixed 28×28 square: the icon centres inside it, so the button's box
          // is the same width in every row and its right edge lines up with the
          // rest of the column regardless of how the glyph is drawn.
          width: 28, height: 28, padding: 0, boxSizing: 'border-box',
          background: 'none', border: 0, borderRadius: 7,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          cursor: 'pointer', color: T.fg7, lineHeight: 0,
          // Kill the black UA focus ring. Chrome paints it after a plain click
          // here, which reads as a stray border around the row's ⋮. The
          // keyboard-only ring below keeps this reachable without a mouse.
          outline: 'none',
        }}
        onMouseEnter={(e) => { e.currentTarget.style.background = T.bgMuted; }}
        onMouseLeave={(e) => { e.currentTarget.style.background = 'none'; }}
        onFocus={(e) => {
          // Inline styles can't express :focus-visible, so match against it
          // directly and ring up only for keyboard focus. try/catch because
          // browsers without the selector throw a SyntaxError on matches().
          try {
            if (e.currentTarget.matches(':focus-visible')) {
              e.currentTarget.style.boxShadow = `0 0 0 2px ${T.orange}55`;
            }
          } catch { /* no :focus-visible support — skip the ring */ }
        }}
        onBlur={(e) => { e.currentTarget.style.boxShadow = 'none'; }}
      >
        <MoreVertical size={size} />
      </button>
      {pos && createPortal(
        <div
          role="menu"
          onClick={(e) => e.stopPropagation()}
          style={{
            position: 'fixed', top: pos.top, right: pos.right, zIndex: 1000, minWidth: 160,
            background: 'var(--em-bg-card)', border: `1px solid ${T.border}`, borderRadius: 10,
            boxShadow: '0 12px 32px -8px rgba(10,10,10,0.22)', padding: 5,
          }}
        >
          {visible.map((it) => (
            <button
              key={it.key || it.label}
              role="menuitem"
              type="button"
              disabled={it.disabled}
              onClick={() => { setPos(null); it.onClick?.(); }}
              style={{
                display: 'flex', alignItems: 'center', gap: 9, width: '100%', textAlign: 'left',
                padding: '8px 10px', borderRadius: 7, border: 0, background: 'transparent',
                cursor: it.disabled ? 'not-allowed' : 'pointer', opacity: it.disabled ? 0.45 : 1,
                color: it.danger ? T.danger : T.fg2, font: `500 12.5px ${T.font}`,
                outline: 'none',   // hover/keyboard highlight below stands in for the UA ring
              }}
              onFocus={(e) => { if (!it.disabled) e.currentTarget.style.background = T.bgMuted; }}
              onBlur={(e) => { e.currentTarget.style.background = 'transparent'; }}
              onMouseEnter={(e) => { if (!it.disabled) e.currentTarget.style.background = T.bgMuted; }}
              onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
            >
              {it.icon && <it.icon size={14} />}
              {it.label}
            </button>
          ))}
        </div>,
        document.body,
      )}
    </>
  );
};

export const Panel = ({ title, action, padding = '18px 20px', children, style = {} }) => (
  <div style={{ background: 'var(--em-bg-card)', border: `1px solid ${T.border}`, borderRadius: 14, padding, boxShadow: T.shadowSm, ...style }}>
    {(title || action) && (
      <div style={{ display: 'flex', alignItems: 'center', marginBottom: 14 }}>
        <span style={{ font: `700 14px ${T.font}`, color: T.fg1, flex: 1 }}>{title}</span>
        {action}
      </div>
    )}
    {children}
  </div>
);

// Compact field metrics for EmployeeMultiSelect (matches the Timesheets filter column).
const FIELD_SM = { padding: "6px 10px", font: `400 12px/1.4 ${T.font}` };
const SELECT_SM = { padding: "6px 28px 6px 10px", font: `500 12px/1.4 ${T.font}` };

// Employee filter — pick any number of people; none picked means everyone.
// Email is shown under each name because usernames repeat (several accounts
// are literally "Test"), and search matches either.
export function EmployeeMultiSelect({ users, value, onChange }) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const ref = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    const onDown = (ev) => { if (ref.current && !ref.current.contains(ev.target)) setOpen(false); };
    const onKey = (ev) => { if (ev.key === "Escape") setOpen(false); };
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  const chosen = new Set(value.map(String));
  const toggle = (id) => {
    const k = String(id);
    onChange(chosen.has(k) ? value.filter((v) => String(v) !== k) : [...value, k]);
  };
  const q = query.trim().toLowerCase();
  // Full names (personName) — usernames are often just a first name, so
  // several people would read "Ajay". Search matches name, username or email.
  const shown = q
    ? users.filter((u) =>
        `${u.full_name || ""} ${u.username || ""} ${u.email || ""}`.toLowerCase().includes(q))
    : users;

  const label = !value.length
    ? "Everyone"
    : value.length === 1
      ? personName(users.find((u) => String(u.id) === String(value[0]))) || "1 employee"
      : `${value.length} employees`;

  const row = {
    display: "flex",
    alignItems: "center",
    gap: 8,
    width: "100%",
    padding: "7px 10px",
    border: 0,
    background: "transparent",
    cursor: "pointer",
    textAlign: "left",
    borderRadius: 6,
    margin: 0,
  };

  return (
    <div ref={ref} style={{ position: "relative" }}>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-haspopup="listbox"
        aria-expanded={open}
        style={{
          ...SELECT_SM,
          boxSizing: "border-box",
          width: "100%",
          display: "flex",
          alignItems: "center",
          gap: 6,
          margin: 0,
          border: `1px solid ${T.border}`,
          borderRadius: 8,
          background: "var(--em-bg-card)",
          color: T.fg1,
          cursor: "pointer",
          textAlign: "left",
          position: "relative",
        }}
      >
        <span style={{ flex: 1, minWidth: 0, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
          {label}
        </span>
        <ChevronDown size={14} color={T.fg5} style={{ position: "absolute", right: 10, top: "50%", transform: "translateY(-50%)", margin: 0 }} />
      </button>

      {open && (
        <div
          role="listbox"
          aria-multiselectable="true"
          style={{
            position: "absolute",
            top: "calc(100% + 4px)",
            left: 0,
            right: 0,
            zIndex: 40,
            background: "var(--em-bg-card)",
            border: `1px solid ${T.border}`,
            borderRadius: 10,
            boxShadow: T.shadowMd,
            padding: 6,
          }}
        >
          <input
            autoFocus
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search employees…"
            style={{
              ...FIELD_SM,
              boxSizing: "border-box",
              width: "100%",
              margin: "0 0 6px",
              border: `1px solid ${T.border}`,
              borderRadius: 7,
              outline: "none",
              background: "var(--em-bg-card)",
              color: T.fg1,
            }}
          />
          <div style={{ maxHeight: 240, overflowY: "auto" }}>
            {!q && (
              <button
                type="button"
                onClick={() => onChange([])}
                style={{ ...row, font: `600 12px ${T.font}`, color: value.length ? T.fg3 : T.orange }}
              >
                <input type="checkbox" readOnly checked={!value.length} tabIndex={-1} style={{ margin: 0, accentColor: T.orange, pointerEvents: "none" }} />
                Everyone
              </button>
            )}
            {shown.map((u) => {
              const on = chosen.has(String(u.id));
              return (
                <button
                  key={u.id}
                  type="button"
                  role="option"
                  aria-selected={on}
                  onClick={() => toggle(u.id)}
                  style={{ ...row, background: on ? T.orange50 : "transparent" }}
                >
                  <input type="checkbox" readOnly checked={on} tabIndex={-1} style={{ margin: 0, accentColor: T.orange, pointerEvents: "none", flexShrink: 0 }} />
                  <span style={{ minWidth: 0 }}>
                    <span style={{ display: "block", font: `500 12px ${T.font}`, color: T.fg1, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                      {personName(u) || "—"}
                    </span>
                    {u.email && (
                      <span style={{ display: "block", font: `400 10.5px ${T.font}`, color: T.fg7, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                        {u.email}
                      </span>
                    )}
                  </span>
                </button>
              );
            })}
            {q && !shown.length && (
              <div style={{ padding: "10px", font: `400 12px ${T.font}`, color: T.fg7, textAlign: "center" }}>
                No matching employees
              </div>
            )}
          </div>
          {value.length > 0 && (
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", borderTop: `1px solid ${T.divider}`, marginTop: 6, padding: "6px 4px 0" }}>
              <span style={{ font: `500 11px ${T.font}`, color: T.fg5 }}>{value.length} selected</span>
              <button
                type="button"
                onClick={() => onChange([])}
                style={{ border: 0, background: "transparent", color: T.orange, font: `600 11px ${T.font}`, cursor: "pointer", padding: "2px 4px", margin: 0 }}
              >
                Clear
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
